// Insights.js - Redesigned UX-Focused Recovery Insights
import { useState, useEffect, useMemo } from 'react';
import api from '../../Services/Api';
import styles from './Insights.module.css';
import { HeatMap } from './ChartComponents';
import TimeFilter from './insightsComponents/timeFilter';
import { getPeriodBounds, formatDateISO, formatDateDisplay, getMonthOptions, getYearOptions } from '../../Utils/dateUtils';

function Insights({ userData }) {
  // === State ===
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // === Fetch Data ===
  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, summaryRes] = await Promise.all([
        api.get('stats/overview/'),
        api.get(`stats/period_summary/?start=${formatDateISO(getPeriodBounds('month').start)}&end=${formatDateISO(getPeriodBounds('month').end)}`)
      ]);
      setOverviewData(overviewRes.data);
      setSummaryData(summaryRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodData = async (period, customStart = null, customEnd = null) => {
    try {
      let start, end;
      
      if (customStart && customEnd) {
        start = customStart;
        end = customEnd;
      } else {
        const bounds = getPeriodBounds(period);
        start = bounds.start;
        end = bounds.end;
      }
      
      const response = await api.get(
        `stats/period_summary/?start=${formatDateISO(start)}&end=${formatDateISO(end)}`
      );
      setSummaryData(response.data);
    } catch (err) {
      console.error('Error fetching period data:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setCustomStartDate(null);
    setCustomEndDate(null);
    fetchPeriodData(period);
  };

  const handleCustomRange = (dateValue, type) => {
    const date = new Date(dateValue);
    if (type === 'start') {
      setCustomStartDate(date);
      if (customEndDate && date <= customEndDate) {
        fetchPeriodData('custom', date, customEndDate);
      }
    } else {
      setCustomEndDate(date);
      if (customStartDate && date >= customStartDate) {
        fetchPeriodData('custom', customStartDate, date);
      }
    }
  };

  // === Computed Values ===
  const stats = useMemo(() => {
    if (!overviewData) return null;
    const basic = overviewData.basic_stats || {};
    const psych = overviewData.psychological_insights || {};
    const records = overviewData.personal_records || {};
    const summary = summaryData?.summary || {};
    const comparative = overviewData.comparative_analytics || {};
    const highlights = summaryData?.highlights || {};

    return {
      currentStreak: basic.current_streak || 0,
      longestStreak: basic.longest_streak || 0,
      cleanDays: basic.clean_days || 0,
      totalDays: basic.total_days_logged || 0,
      cleanRate: basic.clean_day_percentage || 0,
      avgFapsPerDay: basic.average_faps_per_day?.toFixed(1) || '0.0',
      totalFaps: basic.total_faps || 0,
      maxFapsInDay: basic.max_faps_in_a_day || 0,
      riskyDays: psych.triggers?.risky_days || [],
      riskLevel: psych.triggers?.current_streak_risk || 'low',
      streakBeforeRelapse: psych.triggers?.common_streak_length_before_relapse || 0,
      recoveryTime: psych.recovery_analysis?.average_recovery_time || 0,
      recoveryTrend: psych.recovery_analysis?.recovery_consistency || 'stable',
      bestMonth: records.best_month || 'N/A',
      cleanestMonthPct: records.cleanest_month_percentage || 0,
      periodCleanDays: summary.clean_days || 0,
      periodTotalDays: summary.total_days || 0,
      periodCleanRate: summary.clean_percentage || 0,
      periodFaps: summary.total_faps || 0,
      periodLongestStreak: summary.longest_clean_streak || 0,
      milestones: overviewData.milestones || [],
      recommendations: overviewData.actionable_recommendations || [],
      heatmapData: overviewData.visual_data?.heatmap_data || overviewData.heatmap_data || [],
      // Comparative
      vsPreviousPeriod: comparative.vs_previous_period?.improvement,
      vsPersonalBest: comparative.vs_personal_best?.percentage_of_best,
      // Highlights
      bestDay: highlights.best_day,
      worstDay: highlights.worst_day,
      // For TimeFilter - extract months with data from heatmap
      monthsWithData: (() => {
        const heatmap = overviewData.visual_data?.heatmap_data || overviewData.heatmap_data || [];
        if (!Array.isArray(heatmap)) return [];
        const monthsSet = new Set();
        heatmap.forEach(item => {
          if (item.date) {
            const d = new Date(item.date);
            monthsSet.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }
        });
        return Array.from(monthsSet);
      })()
    };
  }, [overviewData, summaryData]);

  // === Risk Level Color ===
  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return '#ff6b6b';
      case 'medium': return '#ffa502';
      case 'low': return '#26de81';
      default: return '#a29bfe';
    }
  };

  // === Progress Ring Component ===
  const ProgressRing = ({ percent, size = 120, strokeWidth = 10, color = '#6c5ce7' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percent / 100) * circumference;

    return (
      <svg width={size} height={size} className={styles.progressRing}>
        <circle
          className={styles.progressRingBg}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className={styles.progressRingFill}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ stroke: color }}
        />
        <text x="50%" y="50%" className={styles.progressRingText}>
          {percent}%
        </text>
      </svg>
    );
  };

  // === Loading State ===
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Analyzing your recovery journey...</p>
        </div>
      </div>
    );
  }

  // === Error State ===
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <span className={styles.errorIcon}>⚠️</span>
          <h3>Unable to Load Insights</h3>
          <p>{error}</p>
          <button onClick={fetchData} className={styles.retryBtn}>Try Again</button>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className={styles.container}>
      {/* Hero Section - Key Metrics */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Your Recovery Insights</h1>
          <p className={styles.heroSubtitle}>Data-driven analysis of your journey</p>
        </div>

        {/* Key Stats Row */}
        <div className={styles.keyStatsRow}>
          <div className={styles.keyStat}>
            <div className={styles.keyStatIcon}>🔥</div>
            <div className={styles.keyStatContent}>
              <span className={styles.keyStatValue}>{stats.currentStreak}</span>
              <span className={styles.keyStatLabel}>Current Streak</span>
            </div>
          </div>
          <div className={styles.keyStat}>
            <div className={styles.keyStatIcon}>🏆</div>
            <div className={styles.keyStatContent}>
              <span className={styles.keyStatValue}>{stats.longestStreak}</span>
              <span className={styles.keyStatLabel}>Best Streak</span>
            </div>
          </div>
          <div className={styles.keyStat}>
            <div className={styles.keyStatIcon}>✅</div>
            <div className={styles.keyStatContent}>
              <span className={styles.keyStatValue}>{stats.cleanDays}</span>
              <span className={styles.keyStatLabel}>Total Clean Days</span>
            </div>
          </div>
          <div className={styles.keyStat}>
            <div className={styles.keyStatIcon}>📊</div>
            <div className={styles.keyStatContent}>
              <span className={styles.keyStatValue}>{stats.cleanRate}%</span>
              <span className={styles.keyStatLabel}>Success Rate</span>
            </div>
          </div>
        </div>
      </section>

      {/* Time Filter Section */}
      <section className={styles.filterSection}>
        <TimeFilter
          period={selectedPeriod}
          onPeriodChange={handlePeriodChange}
          onCustomRange={handleCustomRange}
          firstLogDate={overviewData?.basic_stats?.meta?.first_log_date}
          monthsWithData={stats?.monthsWithData || []}
        />
      </section>

      {/* Main Grid Layout */}
      <div className={styles.mainGrid}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* Period Analysis Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>📅 Period Analysis</h2>
              <span className={styles.periodLabel}>
                {selectedPeriod === 'week' && 'This Week'}
                {selectedPeriod === 'month' && 'This Month'}
                {selectedPeriod === 'year' && 'This Year'}
                {selectedPeriod === 'all' && 'All Time'}
                {customStartDate && customEndDate && 'Custom Range'}
              </span>
            </div>

            <div className={styles.periodStats}>
              <div className={styles.periodStatMain}>
                <div className={styles.progressRingWrapper}>
                  <ProgressRing 
                    percent={stats.periodCleanRate} 
                    color={stats.periodCleanRate >= 70 ? '#26de81' : stats.periodCleanRate >= 40 ? '#ffa502' : '#ff6b6b'}
                  />
                </div>
                <div className={styles.periodStatInfo}>
                  <span className={styles.periodStatLabel}>Clean Rate</span>
                  <span className={styles.periodStatDetail}>
                    {stats.periodCleanDays} of {stats.periodTotalDays} days
                  </span>
                </div>
              </div>
              <div className={styles.periodStatGrid}>
                <div className={styles.periodStatItem}>
                  <span className={styles.periodStatValue}>{stats.periodCleanDays}</span>
                  <span className={styles.periodStatLabel}>Clean Days</span>
                </div>
                <div className={styles.periodStatItem}>
                  <span className={styles.periodStatValue}>{stats.periodFaps}</span>
                  <span className={styles.periodStatLabel}>Total Relapses</span>
                </div>
                <div className={styles.periodStatItem}>
                  <span className={styles.periodStatValue}>{stats.periodLongestStreak}</span>
                  <span className={styles.periodStatLabel}>Best Streak</span>
                </div>
              </div>
            </div>

            {/* Comparative Analytics */}
            {(stats.vsPreviousPeriod !== undefined || stats.vsPersonalBest) && (
              <div className={styles.comparativeSection}>
                {stats.vsPreviousPeriod !== undefined && (
                  <div className={styles.compareItem}>
                    <span className={styles.compareLabel}>vs Previous Period</span>
                    <span className={`${styles.compareValue} ${stats.vsPreviousPeriod >= 0 ? styles.positive : styles.negative}`}>
                      {stats.vsPreviousPeriod >= 0 ? '↑' : '↓'} {Math.abs(stats.vsPreviousPeriod)}%
                    </span>
                  </div>
                )}
                {stats.vsPersonalBest && (
                  <div className={styles.compareItem}>
                    <span className={styles.compareLabel}>vs Personal Best</span>
                    <span className={styles.compareValue}>{stats.vsPersonalBest}%</span>
                  </div>
                )}
              </div>
            )}

            {/* Performance Highlights */}
            {(stats.bestDay || stats.worstDay) && (
              <div className={styles.highlightsRow}>
                {stats.bestDay && (
                  <div className={styles.highlightItem}>
                    <span className={styles.highlightIcon}>🌟</span>
                    <div className={styles.highlightInfo}>
                      <span className={styles.highlightLabel}>Best Day</span>
                      <span className={styles.highlightValue}>
                        {formatDateDisplay(stats.bestDay.date, false)}
                      </span>
                    </div>
                  </div>
                )}
                {stats.worstDay && (
                  <div className={styles.highlightItem}>
                    <span className={styles.highlightIcon}>⚠️</span>
                    <div className={styles.highlightInfo}>
                      <span className={styles.highlightLabel}>Toughest Day</span>
                      <span className={styles.highlightValue}>
                        {formatDateDisplay(stats.worstDay.date, false)} ({stats.worstDay.fap_count} relapses)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Risk Analysis Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>⚡ Risk Analysis</h2>
            </div>

            <div className={styles.riskContent}>
              <div 
                className={styles.riskIndicator}
                style={{ borderColor: getRiskColor(stats.riskLevel) }}
              >
                <div 
                  className={styles.riskBadge}
                  style={{ background: getRiskColor(stats.riskLevel) }}
                >
                  {stats.riskLevel.toUpperCase()} RISK
                </div>
                <p className={styles.riskMessage}>
                  {stats.riskLevel === 'high' && "⚠️ Stay alert! You're at a critical point."}
                  {stats.riskLevel === 'medium' && "🔔 Be mindful. Keep your guard up."}
                  {stats.riskLevel === 'low' && "✅ You're doing great! Stay consistent."}
                </p>
              </div>

              {stats.riskyDays.length > 0 && (
                <div className={styles.riskDetail}>
                  <span className={styles.riskDetailLabel}>High-Risk Days</span>
                  <div className={styles.riskyDaysTags}>
                    {stats.riskyDays.map((day, idx) => (
                      <span key={idx} className={styles.riskyDayTag}>{day}</span>
                    ))}
                  </div>
                </div>
              )}

              {stats.streakBeforeRelapse > 0 && (
                <div className={styles.riskDetail}>
                  <span className={styles.riskDetailLabel}>Critical Point</span>
                  <p className={styles.riskDetailText}>
                    Relapses often occur around day <strong>{stats.streakBeforeRelapse}</strong>. 
                    Stay extra cautious when approaching this milestone.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recommendations Card */}
          {stats.recommendations.length > 0 && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>💡 Recommendations</h2>
              </div>
              <div className={styles.recommendationsList}>
                {stats.recommendations.slice(0, 4).map((rec, idx) => (
                  <div key={idx} className={styles.recommendationItem}>
                    <div className={styles.recIcon}>
                      {rec.priority === 'high' ? '🔴' : rec.priority === 'medium' ? '🟡' : '🟢'}
                    </div>
                    <p className={styles.recText}>{rec.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Records Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>🏆 Personal Records</h2>
            </div>
            <div className={styles.recordsGrid}>
              <div className={styles.recordItem}>
                <div className={styles.recordIcon}>🥇</div>
                <div className={styles.recordInfo}>
                  <span className={styles.recordValue}>{stats.longestStreak} days</span>
                  <span className={styles.recordLabel}>Longest Streak</span>
                </div>
              </div>
              <div className={styles.recordItem}>
                <div className={styles.recordIcon}>📅</div>
                <div className={styles.recordInfo}>
                  <span className={styles.recordValue}>{stats.bestMonth}</span>
                  <span className={styles.recordLabel}>Best Month</span>
                </div>
              </div>
              <div className={styles.recordItem}>
                <div className={styles.recordIcon}>✨</div>
                <div className={styles.recordInfo}>
                  <span className={styles.recordValue}>{stats.cleanDays}</span>
                  <span className={styles.recordLabel}>Total Clean Days</span>
                </div>
              </div>
              <div className={styles.recordItem}>
                <div className={styles.recordIcon}>📈</div>
                <div className={styles.recordInfo}>
                  <span className={styles.recordValue}>{stats.cleanRate}%</span>
                  <span className={styles.recordLabel}>Overall Success</span>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>⭐ Milestones</h2>
            </div>
            <div className={styles.milestonesList}>
              {stats.milestones.length > 0 ? (
                stats.milestones.slice(0, 5).map((milestone, idx) => (
                  <div 
                    key={idx} 
                    className={`${styles.milestoneItem} ${milestone.achieved ? styles.milestoneAchieved : ''}`}
                  >
                    <div className={styles.milestoneIcon}>
                      {milestone.achieved ? '✅' : '🔒'}
                    </div>
                    <div className={styles.milestoneInfo}>
                      <span className={styles.milestoneName}>{milestone.message || milestone.name}</span>
                      {milestone.achieved && milestone.achieved_on && (
                        <span className={styles.milestoneDate}>
                          Achieved {formatDateDisplay(milestone.achieved_on, false)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyMilestones}>
                  <span>🎯</span>
                  <p>Keep going to unlock milestones!</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>📊 Quick Stats</h2>
            </div>
            <div className={styles.quickStatsList}>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Total Days Tracked</span>
                <span className={styles.quickStatValue}>{stats.totalDays}</span>
              </div>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Average Faps/Day</span>
                <span className={styles.quickStatValue}>{stats.avgFapsPerDay}</span>
              </div>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Total Relapses</span>
                <span className={styles.quickStatValue}>{stats.totalFaps}</span>
              </div>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Max Faps in a Day</span>
                <span className={styles.quickStatValue}>{stats.maxFapsInDay}</span>
              </div>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Avg Recovery Time</span>
                <span className={styles.quickStatValue}>
                  {stats.recoveryTime > 0 ? `${stats.recoveryTime} days` : '—'}
                </span>
              </div>
              <div className={styles.quickStatRow}>
                <span className={styles.quickStatLabel}>Current Streak vs Best</span>
                <span className={styles.quickStatValue}>
                  {stats.longestStreak > 0 
                    ? `${Math.round((stats.currentStreak / stats.longestStreak) * 100)}%`
                    : '—'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Width HeatMap Section */}
      {stats.heatmapData && stats.heatmapData.length > 0 && (
        <div className={styles.heatmapSection}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>🗓️ Activity Heatmap</h2>
              <span className={styles.heatmapSubtitle}>Your daily progress over time</span>
            </div>
            <div className={styles.heatmapWrapper}>
              <HeatMap data={stats.heatmapData} />
            </div>
            <div className={styles.heatmapLegend}>
              <span className={styles.legendLabel}>Less</span>
              <div className={styles.legendColors}>
                <div className={`${styles.legendBox} ${styles.legendNone}`}></div>
                <div className={`${styles.legendBox} ${styles.legendLight}`}></div>
                <div className={`${styles.legendBox} ${styles.legendMed}`}></div>
                <div className={`${styles.legendBox} ${styles.legendHigh}`}></div>
              </div>
              <span className={styles.legendLabel}>More Relapses</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.lastUpdated}>
          Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        <button onClick={fetchData} className={styles.refreshBtn}>
          🔄 Refresh
        </button>
      </div>
    </div>
  );
}

export default Insights;
